(() => {
  if (window.PinehurstNewArrivals) return;

  const fourteenDays = 14 * 24 * 60 * 60 * 1000;
  const minimumProducts = 12;
  let feedPromise;
  const initializedCollections = new WeakSet();

  const sectionURL = (base, sectionId, page) => {
    const url = new URL(base, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };

  const fetchSection = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error('New Arrivals section unavailable');
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const sortAndSelect = (items) => {
    const ranked = items
      .filter((item) => item.dataset.pinehurstProductId && Number(item.dataset.pinehurstArrival))
      .sort((a, b) => {
        const arrival = Number(b.dataset.pinehurstArrival) - Number(a.dataset.pinehurstArrival);
        if (arrival) return arrival;
        const created = Number(b.dataset.pinehurstCreated) - Number(a.dataset.pinehurstCreated);
        if (created) return created;
        return Number(b.dataset.pinehurstProductId) - Number(a.dataset.pinehurstProductId);
      });
    const cutoff = (Date.now() - fourteenDays) / 1000;
    const recent = ranked.filter((item) => Number(item.dataset.pinehurstArrival) >= cutoff);
    return recent.length >= minimumProducts ? recent : ranked.slice(0, minimumProducts);
  };

  const loadFeed = (collectionUrl) => {
    if (!feedPromise) {
      feedPromise = (async () => {
        const allItems = [];
        let page = 1;
        while (page) {
          const documentPage = await fetchSection(
            sectionURL(collectionUrl, 'pinehurst-mobile-new-arrivals-page', page)
          );
          const section = documentPage.getElementById('PinehurstNewArrivalsPage');
          if (!section) throw new Error('New Arrivals data missing');
          allItems.push(...section.querySelectorAll('li[data-pinehurst-product-id]'));
          const next = Number(section.dataset.nextPage || 0);
          if (next && next <= page) throw new Error('New Arrivals pagination did not advance');
          page = next;
        }
        return sortAndSelect(allItems);
      })().catch((error) => {
        feedPromise = undefined;
        throw error;
      });
    }
    return feedPromise;
  };

  const initCollection = () => {
    if (!/^\/collections\/new-arrivals\/?$/.test(window.location.pathname)) return;
    const grid = document.querySelector('results-list .product-grid[data-testid="product-grid"]');
    const results = grid?.closest('results-list');
    if (!grid || !results || initializedCollections.has(results)) return;
    initializedCollections.add(results);

    (async () => {
      const selected = await loadFeed(window.location.pathname);
      const wanted = new Set(selected.map((item) => item.dataset.pinehurstProductId));
      const cards = new Map();
      const saveCards = (root) => {
        for (const card of root.querySelectorAll('li.product-grid__item[data-product-id]')) {
          if (wanted.has(card.dataset.productId)) cards.set(card.dataset.productId, card);
        }
      };
      saveCards(grid);

      const pages = Number(grid.dataset.lastPage || 1);
      for (let page = 2; page <= pages && cards.size < wanted.size; page += 1) {
        const documentPage = await fetchSection(
          sectionURL(window.location.pathname, results.getAttribute('section-id'), page)
        );
        const nextGrid = documentPage.querySelector('results-list .product-grid[data-testid="product-grid"]');
        if (!nextGrid) throw new Error('New Arrivals cards missing');
        saveCards(nextGrid);
      }
      if (cards.size !== wanted.size) throw new Error('New Arrivals cards do not match collection');

      const orderedCards = selected.map((item) => {
        const card = cards.get(item.dataset.pinehurstProductId);
        return card.ownerDocument === document ? card : document.importNode(card, true);
      });
      grid.replaceChildren(...orderedCards);
      grid.dataset.lastPage = '1';
      results.querySelector('.pagination')?.remove();
      results.dataset.pinehurstArrivalsReady = 'true';
    })().catch(() => {
      initializedCollections.delete(results);
      results.dataset.pinehurstArrivalsReady = 'fallback';
    });
  };

  window.PinehurstNewArrivals = { loadFeed, initCollection };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollection, { once: true });
  } else {
    initCollection();
  }
  document.addEventListener('shopify:section:load', initCollection);
})();
